import { WidgetViewModel } from '../../viewModel/viewModel';
import { BookingsAPI } from '../../api/BookingsApi';
import { ControllerFlowAPI } from '@wix/yoshi-flow-editor';
import { getEnrichedServicesAndAvailability } from '../../utils/getEnrichedServicesAndAvailability/getEnrichedServicesAndAvailability';
import { bookingsUoULoadMorePreviousServicesButtonClickSrc16Evid1222 } from '@wix/bi-logger-bookings-data/v2';
import { ServiceListLoadButtonName } from '../../utils/bi/consts';
import { getLoadingButtonsText } from '../../viewModel/bodyViewModel/bodyViewModel';
import { getPaginationSEOMetadata } from '../../utils/pagination/pagination';
import { groupServicesByCategories } from '@wix/bookings-calendar-catalog-viewer-mapper';
import { isMenuDisplaySelected } from '../../utils/settings/settings';
import { Features } from '@wix/ambassador-bookings-v2-bookings-settings/types';

export type LoadServicesByPageAction = (requestedPage: number) => void;

export const createLoadServicesByPageAction =
  ({
    widgetViewModel,
    bookingsApi,
    flowAPI,
    activeFeatures,
    isPricingPlanInstalled,
    serviceListContext,
    setProps,
  }: {
    widgetViewModel: WidgetViewModel;
    bookingsApi: BookingsAPI;
    setProps: Function;
    flowAPI: ControllerFlowAPI;
    activeFeatures: Features;
    isPricingPlanInstalled: boolean;
    serviceListContext: any;
  }): LoadServicesByPageAction =>
  async (requestedPage) => {
    const { services: currentServices, servicesPagingMetadata } =
      widgetViewModel;

    if (!servicesPagingMetadata) {
      return;
    }

    const { totalPages, maxPageLoaded, minPageLoaded } = servicesPagingMetadata;

    const shouldLoadPreviousPage =
      requestedPage < minPageLoaded && minPageLoaded > 1;

    const shouldLoadNextPage =
      requestedPage > maxPageLoaded && maxPageLoaded < totalPages;

    const focusCardIndex = shouldLoadNextPage ? currentServices.length : 0;

    flowAPI.bi?.report(
      bookingsUoULoadMorePreviousServicesButtonClickSrc16Evid1222({
        pageName: flowAPI.componentName!,
        buttonName: shouldLoadNextPage
          ? ServiceListLoadButtonName.NEXT
          : ServiceListLoadButtonName.PREVIOUS,
      }),
    );

    const selectedOption = widgetViewModel.filterOptions.find(
      (option) => option.isSelected,
    );

    if (shouldLoadPreviousPage || shouldLoadNextPage) {
      try {
        const { services: enrichedLoadedServices, coursesAvailability } =
          await getEnrichedServicesAndAvailability({
            flowAPI,
            activeFeatures,
            isPricingPlanInstalled,
            isAnywhereFlow: serviceListContext.isAnywhereFlow,
            bookingsApi,
            requestedPage,
            selectedFilterOptionId: selectedOption?.id,
          });

        widgetViewModel.coursesAvailability = {
          ...widgetViewModel.coursesAvailability,
          ...coursesAvailability,
        };
        widgetViewModel.services = shouldLoadNextPage
          ? [...currentServices, ...enrichedLoadedServices]
          : [...enrichedLoadedServices, ...currentServices];

        widgetViewModel.servicesPagingMetadata = {
          totalPages,
          maxPageLoaded: shouldLoadNextPage ? requestedPage : maxPageLoaded,
          minPageLoaded: shouldLoadPreviousPage ? requestedPage : minPageLoaded,
        };
        widgetViewModel.groupedServicesByCategories = isMenuDisplaySelected(
          flowAPI.settings,
          flowAPI.experiments,
        )
          ? groupServicesByCategories(widgetViewModel.services)
          : undefined;

        const { loadPreviousButtonText, loadMoreButtonText } =
          getLoadingButtonsText({
            flowAPI,
            servicesPagingMetadata: widgetViewModel.servicesPagingMetadata,
          });

        const { nextUrl, prevUrl } = getPaginationSEOMetadata(
          flowAPI.controllerConfig.wixCodeApi,
          widgetViewModel.servicesPagingMetadata,
        );

        setProps({
          widgetViewModel: {
            ...widgetViewModel,
            bodyViewModel: {
              ...widgetViewModel.bodyViewModel,
              loadPreviousButtonText,
              loadPreviousSeoHref: prevUrl,
              loadMoreButtonText,
              loadMoreSeoHref: nextUrl,
              focusCardIndex,
            },
          },
        });
      } catch (error) {
        console.error(error);

        widgetViewModel.errorMessage = flowAPI.translations.t(
          'error-state.load-buttons.text',
        );
        setProps({
          widgetViewModel: { ...widgetViewModel },
        });
      }
    }
  };
